import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AssetsDropdown from './AssetsDropdown'; // Assuming this is also styled similarly or can be updated
import { DarkLightButton } from '../../../../components/darklightSwitch/DarkLightButton';
import ProfilePopup from '../../../../components/profile/ProfilePopup';
import { MarketStatsBar } from '../../MarketChart/MarketStatsBar';
import { useCurrentPrice, useDarkMode, useIsExRoute } from '../../../../recoil';
import useSWR from 'swr';
import { pingCexServer } from '../../../../services/order.service';
import { TradingDataService } from '../../../../services/trading-data.service';
import { TickerSnapData } from '../../../../types/symbol.types';
import TickerSnapCard from './TickerSnapCard';
import { API_ENDPOINTS } from '../../../../utils/constants';
import {
  getMarketStats,
  getTradingPair,
  getTradingPairs,
} from '../../../../services/cex-engine.service';
import { MarketPairsData, TradingPair } from '../../../../types/cex-trade';
import {
  CexEngineSocketService,
  getCexEngineSocket,
} from '../../../../services/cex-engine-socket.service';

interface Market {
  symbol?: string;
  name?: string;
  baseCurrency?: string;
  quoteCurrency?: string;
  feeCurrency?: string;
  market?: string;
  baseMinSize?: string;
  quoteMinSize?: string;
  baseMaxSize?: string;
  quoteMaxSize?: string;
  baseIncrement?: string;
  quoteIncrement?: string;
  priceIncrement?: string;
  priceLimitRate?: string;
  minFunds?: string;
  isMarginEnabled?: boolean;
  enableTrading?: boolean;
  feeCategory?: number;
  makerFeeCoefficient?: string;
  takerFeeCoefficient?: string;
  st?: boolean;
  callauctionIsEnabled?: boolean;
  callauctionPriceFloor?: string | null;
  callauctionPriceCeiling?: string | null;
  callauctionFirstStageStartTime?: string | null;
  callauctionSecondStageStartTime?: string | null;
  callauctionThirdStageStartTime?: string | null;
  tradingStartTime?: string | null;
  tag?: string;
}

export const mapToMarket =
  (tag = '') =>
  (raw: MarketPairsData): Market => {
    return {
      symbol: raw.symbol ?? '',
      name: raw.description ?? '',
      baseCurrency: raw.baseAsset ?? '',
      quoteCurrency: raw.quoteAsset ?? '',
      feeCurrency: raw.quoteAsset ?? '', // Usually same as quote
      market: raw.symbol ?? '',
      baseMinSize: raw.minOrderSize ?? '0',
      quoteMinSize: raw.minOrderValue ?? '0',
      baseMaxSize: raw.maxOrderSize ?? '0',
      quoteMaxSize: raw.maxOrderValue ?? '0',
      baseIncrement: raw.lotSize ?? '0',
      quoteIncrement: raw.tickSize ?? '0',
      priceIncrement: raw.tickSize ?? '0',
      priceLimitRate: '', // not provided
      minFunds: raw.minOrderValue ?? '0',
      isMarginEnabled: false,
      enableTrading: raw.status === 'ACTIVE',
      feeCategory: 0,
      makerFeeCoefficient: raw.makerFeeRate ?? '0',
      takerFeeCoefficient: raw.takerFeeRate ?? '0',
      st: false,
      callauctionIsEnabled: false,
      callauctionPriceFloor: null,
      callauctionPriceCeiling: null,
      callauctionFirstStageStartTime: null,
      callauctionSecondStageStartTime: null,
      callauctionThirdStageStartTime: null,
      tradingStartTime: raw.createdAt ?? null,
      tag: tag,
    };
  };

const EXTRA_TABS = ['Exotic'];

const TopBar: React.FC<{ isNotMobile: boolean }> = ({ isNotMobile }) => {
  const [darkMode, setDarkMode] = useDarkMode();
  const params = useParams<{ market: string }>();
  const navigate = useNavigate();
  const [currentPrice] = useCurrentPrice();
  const isExotic = useIsExRoute();
  const [marketCategories, setMarketCategories] = useState<string[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<string[]>([]);
  const [allSymbols, setAllSymbols] = useState<Record<string, Market[]>>({}); // Store all symbols by market
  const [currentSymbols, setCurrentSymbols] = useState<Market[]>([]); // Symbols for the selected tab
  const [selectedMarket, setSelectedMarket] = useState('');
  const [selectedTab, setSelectedTab] = useState('');
  const [mainTab, setMainTab] = useState<'favorites' | 'spot'>('spot');
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState<Market[]>(() => {
    try {
      const saved = localStorage.getItem('kucoin-favorites');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading favorites from localStorage:', error);
      return [];
    }
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoadingMarkets, setIsLoadingMarkets] = useState(false);
  const [isLoadingSymbols, setIsLoadingSymbols] = useState(false);
  const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
  const [pageSize] = useState(20);
  const [hasNext, setHasNext] = useState<Record<string, boolean>>({});
  const [totalPages, setTotalPages] = useState<Record<string, number>>({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchedSymbols, setSearchedSymbols] = useState<Market[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const symbolsListRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentHandleScroll = useRef<(() => void) | null>(null);
  // // Ping CEX server every 18 seconds to keep the connection alive
  // useSWR(
  //   ["justPingCexServer"],
  //   pingCexServer,
  //   {
  //     refreshInterval: 18_000 // 18 seconds
  //   });

  // Ticker snap
  const wsRef = useRef<WebSocket | null>(null);
  const serviceRef = useRef<TradingDataService | null>(null);
  const [tickerSnapData, setTickerSnapData] = useState<TickerSnapData | null>(
    null
  );
  const cexEngineServiceRef = useRef<CexEngineSocketService | null>(null);

  useEffect(() => {
    if (!params.market) return;

    const marketNow = params.market; // local variable

    serviceRef.current = new TradingDataService();

    const connectWS = async () => {
      if (!serviceRef.current) return;
      // console.log('Connecting to tickerSnap WebSocket for', marketNow);
      wsRef.current = await serviceRef.current.connectTickerSnapWebSocket(
        marketNow,
        (tickerUpdates) => {
          setTickerSnapData(tickerUpdates);
        }
      );
    };

    if (isExotic) {
      cexEngineServiceRef.current = getCexEngineSocket();
      getMarketStats(marketNow).then((stats) => {
        console.log({ apiStats: stats });
        setTickerSnapData(
          (prev) =>
            ({
              lastTradedPrice: parseFloat(stats?.lastPrice) || 0,
              high: parseFloat(stats?.highPrice) || 0,
              low: parseFloat(stats?.lowPrice) || 0,
              vol: parseFloat(stats?.volume) || 0,
              volValue:
                parseFloat(stats?.lastPrice) * parseFloat(stats?.volume) || 0,
              changeRate: parseFloat(stats?.priceChangePercent) || 0,
            } as TickerSnapData)
        );
      });
      getCexEngineSocket()?.onMarketStatsUpdate((stats) => {
        console.log({ stats });
        setTickerSnapData(
          (prev) =>
            ({
              lastTradedPrice: parseFloat(stats?.lastPrice) || 0,
              high: parseFloat(stats?.highPrice) || 0,
              low: parseFloat(stats?.lowPrice) || 0,
              vol: parseFloat(stats?.volume) || 0,
              volValue:
                parseFloat(stats?.lastPrice) * parseFloat(stats?.volume) || 0,
              changeRate: parseFloat(stats?.priceChangePercent) || 0,
            } as TickerSnapData)
        );
      });
      setTimeout(() => {
        getCexEngineSocket()?.subscribeMarketStats({ symbol: marketNow });
      }, 0);
    } else connectWS();

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [params.market, isExotic]);

  const fetchPaginatedSymbols = async (
    page: number = 1,
    append: boolean = false
  ) => {
    if (!selectedTab) return;
    if (page === 1) setIsLoadingSymbols(true);
    setIsLoadingMore(true);

    // Abort previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    if (EXTRA_TABS.includes(selectedTab)) {
      const tradingPairs = await getTradingPairs({ status: 'ACTIVE' });
      const market = tradingPairs.map(mapToMarket(selectedTab));
      setAllSymbols((prev) => ({
        ...prev,
        [selectedTab]: [...(prev[selectedTab] || []), ...market],
      }));
      setIsLoadingSymbols(false);
      setIsLoadingMore(false);
      return;
    }

    try {
      const requestedTab = selectedTab;
      const response = await fetch(
        `${API_ENDPOINTS.proxy}api/v2/symbols/paginated?market=${selectedTab}&page=${page}&pageSize=${pageSize}`,
        { signal }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (
        data?.data &&
        Array.isArray(data.data) &&
        requestedTab === selectedTab
      ) {
        if (append) {
          setAllSymbols((prev) => ({
            ...prev,
            [selectedTab]: [...(prev[selectedTab] || []), ...data.data],
          }));
        } else {
          setAllSymbols((prev) => ({
            ...prev,
            [selectedTab]: data.data,
          }));
        }
        setHasNext((prev) => ({
          ...prev,
          [selectedTab]: data.pagination?.hasNext || false,
        }));
        setTotalPages((prev) => ({
          ...prev,
          [selectedTab]: data.pagination?.totalPages || 0,
        }));
        setCurrentPage((prev) => ({
          ...prev,
          [selectedTab]: page,
        }));
      } else {
        if (!append) {
          setAllSymbols((prev) => ({
            ...prev,
            [selectedTab]: [],
          }));
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was aborted, do nothing
        return;
      }
      console.error('Failed to fetch paginated symbols:', error);
      if (!append) {
        setAllSymbols((prev) => ({
          ...prev,
          [selectedTab]: [],
        }));
      }
    } finally {
      if (page === 1) setIsLoadingSymbols(false);
      setIsLoadingMore(false);
    }
  };

  const fetchSearchResults = useCallback(
    async (query: string) => {
      if (!selectedTab || query.length < 3) return;
      setIsSearching(true);
      // Abort previous
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
      searchAbortControllerRef.current = new AbortController();
      const signal = searchAbortControllerRef.current.signal;
      try {
        const response = await fetch(
          `${API_ENDPOINTS.proxy}api/v2/symbols/search?q=${encodeURIComponent(
            query
          )}&market=${selectedTab}`,
          { signal }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data?.data && Array.isArray(data.data)) {
          // Filter out duplicates based on symbol
          const uniqueData = data.data.filter(
            // @ts-ignore
            (symbol, index, self) =>
              // @ts-ignore
              self.findIndex((s) => s.symbol === symbol.symbol) === index
          );
          setSearchedSymbols(uniqueData);
        } else {
          setSearchedSymbols([]);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch search results:', error);
        setSearchedSymbols([]);
      } finally {
        setIsSearching(false);
      }
    },
    [selectedTab]
  );

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && mainTab === 'spot' && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen, mainTab]);

  // Fetch search results when search term changes
  useEffect(() => {
    if (searchTerm.length >= 3) {
      fetchSearchResults(searchTerm);
    } else {
      setSearchedSymbols([]);
      setIsSearching(false);
    }
  }, [searchTerm, selectedTab, fetchSearchResults]);

  // Fetch paginated symbols for spot
  useEffect(() => {
    if (!selectedTab || mainTab !== 'spot') return;
    setAllSymbols((prev) => ({
      ...prev,
      [selectedTab]: [],
    }));
    setCurrentPage((prev) => ({
      ...prev,
      [selectedTab]: 1,
    }));
    setHasNext((prev) => ({
      ...prev,
      [selectedTab]: false,
    }));
    fetchPaginatedSymbols(1, false);
  }, [selectedTab, mainTab]);

  // Set currentSymbols
  useEffect(() => {
    if (mainTab === 'spot') {
      setCurrentSymbols(allSymbols[selectedTab] || []);
    } else if (mainTab === 'favorites') {
      setCurrentSymbols([]);
    }
  }, [allSymbols, mainTab, selectedTab]);

  // Filter and sort categories
  const filterAndSortCategories = (categories: string[]) => {
    const desiredOrder = [
      'USDS',
      'BTC',
      'ETF',
      'Stocks',
      'KCS',
      'ALTS',
      ...EXTRA_TABS,
    ];
    const filtered = categories.filter((cat) => desiredOrder.includes(cat));
    return desiredOrder.filter((order) => filtered.includes(order));
  };

  // Fetch markets from KuCoin API
  useEffect(() => {
    const fetchMarkets = async () => {
      setIsLoadingMarkets(true);
      try {
        const response = await fetch(
          `${API_ENDPOINTS.proxy}api/v1/market/markets`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data?.data && Array.isArray(data.data)) {
          setMarketCategories([...data.data, ...EXTRA_TABS]);
          const filtered = filterAndSortCategories([
            ...data.data,
            ...EXTRA_TABS,
          ]);
          setFilteredCategories(filtered);
          // Set default tab
          if (filtered.length > 0) {
            setSelectedTab(filtered[0]);
          }
        } else {
          setMarketCategories([]);
          setFilteredCategories([]);
          setSelectedTab('');
        }
      } catch (error) {
        console.error('Failed to fetch markets:', error);
        setMarketCategories([]);
        setFilteredCategories([]);
        setSelectedTab('');
      } finally {
        setIsLoadingMarkets(false);
      }
    };
    fetchMarkets();
  }, []);

  // Update selectedMarket if URL param changes
  useEffect(() => {
    if (params.market && params.market.toUpperCase() !== selectedMarket) {
      setSelectedMarket(params.market.toUpperCase());
    }
  }, [params.market, selectedMarket]);

  // Update page title on market change
  useEffect(() => {
    let docTitle = '';
    if (currentPrice?.value && currentPrice?.marketParam == params.market)
      docTitle += `${currentPrice.value.toFixed(2)} | `;
    if (params.market) {
      docTitle += `${params.market.toUpperCase()} | `;
    }
    docTitle += 'RVA Exchange';
    document.title = docTitle;
  }, [params.market, currentPrice?.value]);

  // Navigate when market is selected
  const handleMarketSelect = (market: string, lastSelectedMarket = '') => {
    if (market && EXTRA_TABS.includes(lastSelectedMarket)) {
      setSelectedMarket(market);
      setIsDropdownOpen(false);
      navigate(`/ex/${market}`);
      setSearchTerm(''); // Clear search term after selection
    } else if (market) {
      setSelectedMarket(market);
      setIsDropdownOpen(false);
      navigate(`/${market}`);
      setSearchTerm(''); // Clear search term after selection
    }
  };

  // Toggle favorite status
  const toggleFavorite = (market: Market, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering market selection
    setFavorites((prev) => {
      const isFav = prev.some((fav) => fav.symbol === market.symbol);
      if (isFav) {
        return prev.filter((fav) => fav.symbol !== market.symbol);
      } else {
        return [...prev, market];
      }
    });
  };

  // Save favorites to localStorage whenever favorites change
  useEffect(() => {
    try {
      localStorage.setItem('kucoin-favorites', JSON.stringify(favorites));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }, [favorites]);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll handler for loading more symbols
  const handleScroll = useCallback(() => {
    if (!symbolsListRef.current || mainTab !== 'spot') return;
    const { scrollTop, clientHeight, scrollHeight } = symbolsListRef.current;
    if (
      scrollTop + clientHeight >= scrollHeight - 10 &&
      hasNext[selectedTab] &&
      !isLoadingMore
    ) {
      fetchPaginatedSymbols((currentPage[selectedTab] || 1) + 1, true);
    }
  }, [
    hasNext[selectedTab],
    currentPage[selectedTab],
    isLoadingMore,
    selectedTab,
    mainTab,
  ]);

  useEffect(() => {
    const div = symbolsListRef.current;
    if (div) {
      // Remove previous listener if exists
      if (currentHandleScroll.current) {
        div.removeEventListener('scroll', currentHandleScroll.current);
      }
      // Add new listener
      currentHandleScroll.current = handleScroll;
      div.addEventListener('scroll', handleScroll);
      return () => {
        if (currentHandleScroll.current) {
          div.removeEventListener('scroll', currentHandleScroll.current);
          currentHandleScroll.current = null;
        }
      };
    }
  }, [handleScroll]);

  // Filtered symbols for display (spot tab)
  const filteredSpotSymbols = useMemo(() => {
    // Remove duplicates based on symbol
    const uniqueSymbols = currentSymbols.filter(
      (symbol, index, self) =>
        self.findIndex((s) => s.symbol === symbol.symbol) === index
    );
    // console.log({ uniqueSymbols });
    return uniqueSymbols.filter((symbol) =>
      (symbol.symbol || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [currentSymbols, searchTerm]);

  // Favorite symbols for display (favorites tab)
  const favoriteSymbolsData = useMemo(() => {
    return favorites;
  }, [favorites]);

  // Display symbols based on search
  const displaySymbols = useMemo(() => {
    if (searchTerm.length >= 3) {
      return searchedSymbols;
    } else {
      return filteredSpotSymbols;
    }
  }, [searchTerm, searchedSymbols, filteredSpotSymbols]);

  return (
    <div className="w-full  flex items-center px-4 py-2 pb-3 bg-white dark:!bg-[#121212] text-[#121212] dark:!text-white">
      {/* Logo */}
      <div className="flex items-center space-x-6">
        <img
          // src={`${API_ENDPOINTS.trade}${
          //   darkMode ? 'logo.webp' : 'logo-dark.webp'
          // }`}
          src="logo.svg"
          alt="Logo"
          className="h-[40px] cursor-pointer transition-transform duration-200 hover:scale-105"
          onClick={() => window.location.assign(`/`)}
        />

        {/* Market Dropdown */}
        <div
          className={`relative pt-2 ${isNotMobile ? 'pl-[80px]' : ''}`}
          ref={dropdownRef}
        >
          <button
            onClick={() => {
              if (marketCategories.length > 0) {
                setIsDropdownOpen(!isDropdownOpen);
                setSearchTerm(''); // Clear search when opening
              }
            }}
            className="group outline-none flex items-center rounded-lg p-2 text-[15px] font-semibold  transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={marketCategories.length === 0}
          >
            {selectedMarket && (
              <img
                src={`https://assets.coincap.io/assets/icons/${(
                  selectedMarket.split('-')[1] ||
                  selectedMarket.split('/')[0] || // Handle BTC/USDT format
                  'btc'
                ).toLowerCase()}@2x.png`}
                alt=""
                className="mr-2 h-[20px] w-[20px] rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = 'https://via.placeholder.com/20?text=C'; // Fallback to a placeholder
                  e.currentTarget.style.display = 'block'; // Ensure it's visible
                }}
              />
            )}
            <span className="truncate max-w-[120px]">
              {selectedMarket || 'Select Market'}
            </span>
            <span className="ml-2 transition-transform duration-220 group-hover:rotate-180">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={isDropdownOpen ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                />
              </svg>
            </span>
          </button>

          {isDropdownOpen && (
            <div
              className={`absolute z-[99999] mt-3    overflow-hidden bg-[#f1f1f1] dark:bg-cardColor shadow-2xl ring-1 ring-hoverBg animate-slide-down-fade  ${
                isNotMobile
                  ? 'w-[450px] max-h-[70vh] left-[80px]'
                  : 'w-[90vw] max-h-[90vh] left-[-40vw]'
              } `}
              onClick={(e) => e.stopPropagation()}
              style={{ borderRadius: '8px' }}
            >
              {/* Main Tabs */}
              <div className="flex rounded-t-xl">
                <button
                  onClick={() => setMainTab('favorites')}
                  className={`flex-1 px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                    mainTab === 'favorites'
                      ? 'text-brand bg-blue-600 border-b-2 border-brand'
                      : 'text-[#6b7280] dark:hover:text-white hover:bg-hoverBg'
                  }`}
                >
                  <span className="mr-2 text-brand">⭐</span> Favorites
                </button>
                <button
                  onClick={() => setMainTab('spot')}
                  className={`flex-1 px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                    mainTab === 'spot'
                      ? 'text-brand bg-blue-600   border-b-2  border-brand'
                      : 'text-[#6b7280] dark:hover:text-white hover:bg-hoverBg'
                  }`}
                >
                  Spot
                </button>
              </div>

              {/* Content based on main tab */}
              {mainTab === 'favorites' ? (
                <div className="max-h-[calc(70vh-50px)] overflow-y-auto custom-scrollbar">
                  {favoriteSymbolsData.length > 0 ? (
                    <div className="p-2">
                      {favoriteSymbolsData.map((symbolData) => (
                        <div
                          key={symbolData.symbol}
                          className="flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-hoverBg transition-colors duration-150 mb-1 last:mb-0"
                          onClick={() =>
                            handleMarketSelect(
                              symbolData.symbol || '',
                              selectedMarket
                            )
                          }
                        >
                          {/* Star Button - Left Side */}
                          <div className="flex-shrink-0 mr-3">
                            <button
                              onClick={(e) => toggleFavorite(symbolData, e)}
                              className="p-1 rounded-full text-brand hover:bg-hoverBg focus:outline-none focus:ring-2 brand-ring transition-colors"
                              title="Remove from favorites"
                            >
                              <svg
                                className="w-5 h-5 fill-current"
                                viewBox="0 0 24 24"
                              >
                                <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                              </svg>
                            </button>
                          </div>

                          {/* Symbol Information - Middle */}
                          <div className="flex-1 min-w-0 mr-4">
                            <div className="text-base font-semibol flex items-center truncate">
                              <img
                                src={`https://assets.coincap.io/assets/icons/${(
                                  symbolData.symbol?.split('-')[1] ||
                                  symbolData.symbol?.split('/')[0] ||
                                  'btc'
                                ).toLowerCase()}@2x.png`}
                                alt=""
                                className="mr-2 h-[20px] w-[20px] rounded-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src =
                                    'https://via.placeholder.com/20?text=C';
                                }}
                              />
                              {symbolData.symbol || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-400 mt-1 truncate">
                              {symbolData.baseCurrency || 'N/A'}/
                              {symbolData.quoteCurrency || 'N/A'}
                            </div>
                          </div>

                          {/* Trading Info - Right Side */}
                          {symbolData && (
                            <div className="flex flex-col items-end text-right text-xs text-gray-400 space-y-1">
                              <div className="flex items-center space-x-2">
                                {symbolData.enableTrading !== false ? (
                                  <span className="text-green-400 text-[10px] bg-green-900/40 px-2 py-0.5 rounded-full">
                                    Active
                                  </span>
                                ) : (
                                  <span className="text-red-400 text-[10px] bg-red-900/40 px-2 py-0.5 rounded-full">
                                    Paused
                                  </span>
                                )}
                                {symbolData.isMarginEnabled && (
                                  <span className="text-blue-400 text-[10px] bg-blue-900/40 px-2 py-0.5 rounded-full">
                                    Margin
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-2 text-gray-500">
                                <span>
                                  M: {symbolData.makerFeeCoefficient || '0'}%
                                </span>
                                <span>
                                  T: {symbolData.takerFeeCoefficient || '0'}%
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-400">
                      <div className="text-5xl mb-4 opacity-70">⭐</div>
                      <h3 className="text-xl font-semibold  mb-2">
                        No Favorites Yet
                      </h3>
                      <p className="text-sm px-4">
                        Click the star icon next to any trading pair to add it
                        to your favorites and find them here easily.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Market Tabs */}
                  <div className="flex border-b border-[#000] overflow-x-auto bg-gray-850 py-2 px-2 custom-scrollbar-horizontal">
                    {isLoadingMarkets ? (
                      <div className="flex items-center px-4 py-2 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400 mr-2"></div>
                        Loading markets...
                      </div>
                    ) : (
                      filteredCategories.map((market) => (
                        <button
                          key={market}
                          onClick={() => {
                            setSelectedTab(market);
                            setSearchTerm(''); // Clear search when changing market tab
                          }}
                          className={`flex-shrink-0 px-4 py-2 mx-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                            selectedTab === market
                              ? 'text-brand bg-blue-700'
                              : 'text-gray-400 dark:hover:text-white hover:bg-hoverBg'
                          }`}
                        >
                          {market}
                        </button>
                      ))
                    )}
                  </div>

                  {/* Search */}
                  <div className="p-3 border-b border-[#000] bg-gray-850">
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full px-4 py-2 pl-10 outline-none dark:bg-[#1c1c1c]  rounded-lg  dark:placeholder-gray-400 text-sm"
                        placeholder="Search symbols..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Symbols list */}
                  <div
                    className="max-h-[calc(70vh-160px)] overflow-y-auto custom-scrollbar"
                    ref={symbolsListRef}
                  >
                    {(isLoadingSymbols && searchTerm.length < 3) ||
                    (isSearching && searchTerm.length >= 3) ? (
                      <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-3"></div>
                        <span className="text-lg font-medium">
                          {searchTerm.length >= 3
                            ? 'Searching...'
                            : `Loading symbols for ${selectedTab}...`}
                        </span>
                        <p className="text-sm mt-1">
                          {searchTerm.length >= 3
                            ? 'Please wait.'
                            : 'Please wait a moment.'}
                        </p>
                      </div>
                    ) : (
                      <>
                        {displaySymbols.length > 0 ? (
                          <div className="p-2">
                            {displaySymbols.map((symbol) => (
                              <div
                                key={symbol.symbol}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-hoverBg transition-colors duration-150 mb-1 last:mb-0 ${
                                  (isExotic
                                    ? EXTRA_TABS.includes(selectedTab)
                                    : !EXTRA_TABS.includes(selectedTab)) &&
                                  selectedMarket === symbol.symbol
                                    ? 'bg-blue-800/40 ring-1 ring-blue-500'
                                    : ''
                                }`}
                                onClick={() =>
                                  handleMarketSelect(
                                    symbol.symbol || '',
                                    symbol.tag
                                  )
                                }
                              >
                                {/* Star Button - Left Side */}
                                <div className="flex-shrink-0 mr-3">
                                  <button
                                    onClick={(e) => toggleFavorite(symbol, e)}
                                    className="p-1 rounded-full hover:bg-hoverBg focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors"
                                    title={
                                      favorites.some(
                                        (fav) => fav.symbol === symbol.symbol
                                      )
                                        ? 'Remove from favorites'
                                        : 'Add to favorites'
                                    }
                                  >
                                    <svg
                                      className={`w-5 h-5 ${
                                        favorites.some(
                                          (fav) => fav.symbol === symbol.symbol
                                        )
                                          ? 'text-brand fill-current'
                                          : 'text-gray-500 hover:text-brand'
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                      />
                                    </svg>
                                  </button>
                                </div>

                                {/* Symbol Information - Middle */}
                                <div className="flex-1 min-w-0 mr-4">
                                  <div className="text-base font-semibold  flex items-center truncate">
                                    <img
                                      src={`https://assets.coincap.io/assets/icons/${(
                                        symbol.symbol?.split('-')[1] ||
                                        symbol.symbol?.split('/')[0] ||
                                        'btc'
                                      ).toLowerCase()}@2x.png`}
                                      alt=""
                                      className="mr-2 h-[20px] w-[20px] rounded-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src =
                                          'https://via.placeholder.com/20?text=C';
                                      }}
                                    />
                                    {symbol.symbol || 'Unknown'}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1 truncate">
                                    {symbol.baseCurrency || 'N/A'}/
                                    {symbol.quoteCurrency || 'N/A'} • Min:{' '}
                                    {symbol.baseMinSize || 'N/A'}
                                  </div>
                                </div>

                                {/* Trading Info - Right Side */}
                                <div className="flex flex-col items-end text-right text-xs text-gray-400 space-y-1">
                                  <div className="flex items-center space-x-2">
                                    {symbol.enableTrading !== false ? (
                                      <span className="text-green-400 text-[10px] bg-green-900/40 px-2 py-0.5 rounded-full">
                                        Active
                                      </span>
                                    ) : (
                                      <span className="text-red-400 text-[10px] bg-red-900/40 px-2 py-0.5 rounded-full">
                                        Paused
                                      </span>
                                    )}
                                    {symbol.isMarginEnabled && (
                                      <span className="text-blue-400 text-[10px] bg-blue-900/40 px-2 py-0.5 rounded-full">
                                        Margin
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-2 text-gray-500">
                                    <span>
                                      M: {symbol.makerFeeCoefficient || '0'}%
                                    </span>
                                    <span>
                                      T: {symbol.takerFeeCoefficient || '0'}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-gray-400 py-8">
                            <div className="text-3xl mb-3 opacity-80">
                              {searchTerm ? '🔍' : '📊'}
                            </div>
                            <h3 className="text-lg font-medium text-white mb-1">
                              {searchTerm
                                ? 'No results found'
                                : 'No symbols available'}
                            </h3>
                            <p className="text-sm px-4">
                              {searchTerm
                                ? `No trading pairs match "${searchTerm}". Try a different search term.`
                                : `There are no trading symbols for the "${selectedTab}" market. Please select another category.`}
                            </p>
                          </div>
                        )}
                        {isLoadingMore && (
                          <div className="flex items-center px-4 py-2 text-sm text-gray-500 w-full justify-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400 mr-2"></div>
                            Loading more...
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <TickerSnapCard data={tickerSnapData} market={params.market || ''} />
      </div>

      {/* Right Side */}
      <div className="ml-auto flex items-center justify-center gap-3">
        {/* {isNotMobile && <AssetsDropdown />} */}
        <ProfilePopup />
        {isNotMobile && (
          <>
            {/* <MarketStatsBar /> */}
            <DarkLightButton />
          </>
        )}
      </div>
    </div>
  );
};

export default TopBar;
